import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { FormattedMessage } from 'react-intl';
import { exportOrganizations, getOrganizationExportOptions } from '../../../api/clients/organizations';
import { ReportDimensionFilter } from '../../../pages/reports/components/ReportDimensionFilter';
import '../../../pages/reports/components/ReportFilters.scss';

const BUCHAREST_TIMEZONE = 'Europe/Bucharest';
const CUSTOM_PERIOD = 'custom';
const PERIODS = [
  'current_month',
  'last_30_days',
  'current_quarter',
  'current_year',
  CUSTOM_PERIOD,
];

export function getBucharestToday(referenceDate = moment()) {
  return momentTimezone(referenceDate).tz(BUCHAREST_TIMEZONE).format('YYYY-MM-DD');
}

export function datesForExportPeriod(period, today = getBucharestToday()) {
  const end = momentTimezone.tz(today, 'YYYY-MM-DD', true, BUCHAREST_TIMEZONE);
  const start = end.clone();

  if (period === 'last_30_days') {
    start.subtract(29, 'days');
  } else if (period === 'current_quarter') {
    start.startOf('quarter');
  } else if (period === 'current_year') {
    start.startOf('year');
  } else {
    start.startOf('month');
  }

  return {
    dateFrom: start.format('YYYY-MM-DD'),
    dateTo: end.format('YYYY-MM-DD'),
  };
}

export function validateCustomDates(dateFrom, dateTo, today) {
  if (!dateFrom || !dateTo) {
    return 'components.pages.organizations.export.validation.required';
  }

  const from = moment(dateFrom, 'YYYY-MM-DD', true);
  const to = moment(dateTo, 'YYYY-MM-DD', true);

  if (!from.isValid() || !to.isValid()) {
    return 'components.pages.organizations.export.validation.invalid';
  }

  if (from.isAfter(to, 'day')) {
    return 'components.pages.organizations.export.validation.order';
  }

  if (from.isAfter(today, 'day') || to.isAfter(today, 'day')) {
    return 'components.pages.organizations.export.validation.future';
  }

  return null;
}

export function getExportFilename(headers = {}) {
  const disposition = headers['content-disposition'] || headers['Content-Disposition'] || '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const basic = disposition.match(/filename=(?:"([^"]+)"|([^;]+))/i);
  let filename = encoded ? encoded[1] : basic && (basic[1] || basic[2]);

  if (!filename) {
    return 'organizations.csv';
  }

  try {
    filename = decodeURIComponent(filename.trim());
  } catch (_error) {
    filename = filename.trim();
  }

  return filename || 'organizations.csv';
}

export function downloadCsvResponse(response) {
  const headers = response.headers || {};
  const contentType = headers['content-type'] || headers['Content-Type'] || 'text/csv;charset=utf-8';
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: contentType });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  try {
    link.href = downloadUrl;
    link.setAttribute('download', getExportFilename(headers));
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }
}

function optionFromOrganization(organization) {
  if (!organization || !organization.id) {
    return null;
  }

  return {
    label: organization.name || String(organization.id),
    value: String(organization.id),
  };
}

function OrganizationsExport({ today: providedToday }) {
  const [today, setToday] = useState(() => providedToday || getBucharestToday());
  const initialDates = useMemo(() => datesForExportPeriod('current_month', today), [today]);
  const [period, setPeriod] = useState('current_month');
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [organizationMode, setOrganizationMode] = useState('all');
  const [organizationIds, setOrganizationIds] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [catalogState, setCatalogState] = useState('idle');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const catalogRequest = useRef(0);
  const filterGeneration = useRef(0);
  const validationError = validateCustomDates(dateFrom, dateTo, today);

  useEffect(() => () => {
    catalogRequest.current += 1;
  }, []);

  const markFilterChanged = () => {
    filterGeneration.current += 1;
    setStatus(null);
  };

  const refreshCalendar = useCallback((nextPeriod = period) => {
    const currentToday = providedToday || getBucharestToday();
    const dates = nextPeriod === CUSTOM_PERIOD
      ? { dateFrom, dateTo }
      : datesForExportPeriod(nextPeriod, currentToday);
    setToday(currentToday);

    if (dates.dateFrom !== dateFrom || dates.dateTo !== dateTo) {
      setDateFrom(dates.dateFrom);
      setDateTo(dates.dateTo);
      filterGeneration.current += 1;
      setStatus(null);
    }

    return { ...dates, today: currentToday };
  }, [dateFrom, dateTo, period, providedToday]);

  useEffect(() => {
    if (providedToday) {
      refreshCalendar();
      return undefined;
    }

    let timer;
    const refreshAndSchedule = () => {
      const now = momentTimezone().tz(BUCHAREST_TIMEZONE);
      refreshCalendar();
      clearTimeout(timer);
      const nextMidnight = now.clone().add(1, 'day').startOf('day');
      timer = setTimeout(refreshAndSchedule, nextMidnight.diff(now));
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshAndSchedule();
      }
    };

    refreshAndSchedule();
    window.addEventListener('focus', refreshAndSchedule);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', refreshAndSchedule);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [providedToday, refreshCalendar]);

  const loadOrganizations = useCallback(async () => {
    const request = catalogRequest.current + 1;
    catalogRequest.current = request;
    setCatalogState('loading');

    try {
      const response = await getOrganizationExportOptions();
      if (!response || !Array.isArray(response.data)) {
        throw new Error('Invalid organizations response');
      }

      if (catalogRequest.current === request) {
        setOrganizationOptions(response.data.map(optionFromOrganization).filter(Boolean));
        setCatalogState('ready');
      }
    } catch (_error) {
      if (catalogRequest.current === request) {
        setCatalogState('error');
      }
    }
  }, []);

  const toggleAdvanced = () => {
    const nextOpen = !advancedOpen;
    setAdvancedOpen(nextOpen);
    if (nextOpen && catalogState === 'idle') {
      loadOrganizations();
    }
  };

  const updatePeriod = event => {
    const nextPeriod = event.target.value;
    setPeriod(nextPeriod);
    refreshCalendar(nextPeriod);
    markFilterChanged();
  };

  const updateDate = setter => event => {
    setter(event.target.value);
    setPeriod(CUSTOM_PERIOD);
    markFilterChanged();
  };

  const updateOrganizationMode = mode => {
    setOrganizationMode(mode);
    if (mode === 'all') {
      setOrganizationIds([]);
    }
    markFilterChanged();
  };

  const updateOrganizationIds = ids => {
    setOrganizationIds(ids);
    markFilterChanged();
  };

  const reset = () => {
    refreshCalendar('current_month');
    setPeriod('current_month');
    setOrganizationMode('all');
    setOrganizationIds([]);
    markFilterChanged();
  };

  const download = async () => {
    if (busy) {
      return;
    }
    const dates = refreshCalendar();
    if (validateCustomDates(dates.dateFrom, dates.dateTo, dates.today)) {
      return;
    }

    setBusy(true);
    setStatus(null);
    const requestGeneration = filterGeneration.current;

    try {
      const response = await exportOrganizations({
        period,
        dateFrom: dates.dateFrom,
        dateTo: dates.dateTo,
        organizationMode,
        organizationIds,
      });
      downloadCsvResponse(response);
      if (filterGeneration.current === requestGeneration) {
        setStatus('success');
      }
    } catch (_error) {
      if (filterGeneration.current === requestGeneration) {
        setStatus('error');
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = event => {
    event.preventDefault();
    download();
  };

  const advancedCount = organizationMode === 'all' ? 0 : Math.max(1, organizationIds.length);

  return (
    <form aria-busy={busy} className="organizations-export report-filters" onSubmit={submit}>
      <div className="organizations-export__heading">
        <h2><FormattedMessage id="components.pages.organizations.export.title"/></h2>
        <p><FormattedMessage id="components.pages.organizations.export.description"/></p>
      </div>

      <div className="report-filters__primary organizations-export__primary">
        <div className="report-filter-field report-filter-field--period">
          <label htmlFor="organizations-export-period">
            <FormattedMessage id="components.pages.organizations.export.period"/>
          </label>
          <select id="organizations-export-period" onChange={updatePeriod} value={period}>
            {PERIODS.map(value => (
              <FormattedMessage id={`components.pages.organizations.export.period.${value}`} key={value}>
                {message => <option value={value}>{message}</option>}
              </FormattedMessage>
            ))}
          </select>
        </div>
        <div className="report-filter-field">
          <label htmlFor="organizations-export-date-from">
            <FormattedMessage id="components.pages.organizations.export.dateFrom"/>
          </label>
          <input
            id="organizations-export-date-from"
            max={dateTo && dateTo < today ? dateTo : today}
            onChange={updateDate(setDateFrom)}
            required
            type="date"
            value={dateFrom}
          />
        </div>
        <div className="report-filter-field">
          <label htmlFor="organizations-export-date-to">
            <FormattedMessage id="components.pages.organizations.export.dateTo"/>
          </label>
          <input
            id="organizations-export-date-to"
            max={today}
            min={dateFrom || undefined}
            onChange={updateDate(setDateTo)}
            required
            type="date"
            value={dateTo}
          />
        </div>
      </div>

      <button
        aria-controls="organizations-export-advanced-filters"
        aria-expanded={advancedOpen}
        className="report-filters__advanced-trigger"
        onClick={toggleAdvanced}
        type="button"
      >
        <span><FormattedMessage id="components.pages.reports.filters.advanced"/></span>
        {advancedCount > 0 && (
          <span aria-label={`${advancedCount}`} className="report-filters__advanced-count">{advancedCount}</span>
        )}
        <i aria-hidden="true" className={`fas fa-chevron-${advancedOpen ? 'up' : 'down'}`}/>
      </button>

      {advancedOpen && <div
        aria-busy={catalogState === 'loading'}
        className="report-filters__dimensions organizations-export__dimensions"
        id="organizations-export-advanced-filters"
      >
        <div
          aria-labelledby="organizations-label"
          className="report-dimension-card report-dimension-card--organizations"
          role="group"
        >
          {catalogState === 'loading' && (
            <p className="organizations-export__catalog-status" role="status">
              <FormattedMessage id="components.pages.organizations.export.catalogLoading"/>
            </p>
          )}
          {catalogState === 'error' && (
            <div className="organizations-export__catalog-status organizations-export__catalog-status--error">
              <p role="alert"><FormattedMessage id="components.pages.organizations.export.catalogError"/></p>
              <button className="btn btn-outline-primary" onClick={loadOrganizations} type="button">
                <FormattedMessage id="components.pages.organizations.export.retry"/>
              </button>
            </div>
          )}
          {catalogState === 'ready' && <ReportDimensionFilter
            id="organizations"
            labelId="components.pages.reports.filters.organizations"
            mode={organizationMode}
            onModeChange={updateOrganizationMode}
            onSelectionChange={updateOrganizationIds}
            options={organizationOptions}
            selectedIds={organizationIds}
          />}
        </div>
      </div>}

      <div className="report-filters__footer">
        <div aria-live="polite" className="report-filters__status">
          {validationError && <span className="organizations-export__validation" role="alert">
            <FormattedMessage id={validationError}/>
          </span>}
          {!validationError && status && <span
            className={`organizations-export__status organizations-export__status--${status}`}
            role={status === 'error' ? 'alert' : 'status'}
          >
            <FormattedMessage id={`components.pages.organizations.export.${status}`}/>
          </span>}
        </div>
        <button className="btn btn-outline-primary" disabled={busy} onClick={reset} type="button">
          <FormattedMessage id="components.pages.reports.filters.reset"/>
        </button>
        {status === 'error' && <button className="btn btn-outline-primary" disabled={busy} onClick={download} type="button">
          <FormattedMessage id="components.pages.organizations.export.retry"/>
        </button>}
        <button className="btn btn-primary" disabled={busy || Boolean(validationError)} type="submit">
          <FormattedMessage id={busy
            ? 'components.pages.organizations.export.exporting'
            : 'components.pages.organizations.export.download'}/>
        </button>
      </div>
    </form>
  );
}

export { OrganizationsExport };
